/**
 * Display-name rendering for any person-shaped record (User, DirectoryUser,
 * BoardMember, etc.). The CO is referred to by their role label "CO" — not
 * their personal name — because the platform speaks in battalion vocabulary:
 * orders, comments, mentions, AI drafts and reports all read like the CO
 * commanded them, not like the seeded person did. Officers and operators
 * render as `<rank> <name>` (rank optional).
 *
 * The raw `name` column is NEVER edited. The CO's profile-edit form still
 * binds to the literal name on `PublicUser.name`; this helper is the
 * presentation layer used by every read-only render site.
 */

import { Role } from './roles.js';
import { initialsFromName } from './avatar.js';
import {
  APPOINTMENT_LABEL,
  isBattalionAppointment,
  type BattalionAppointment,
} from './appointments.js';

/** Minimal shape every render site can satisfy. */
export interface DisplayNameUser {
  role: string;
  name: string;
  rank?: string | null;
}

/** The label shown for the CO everywhere except profile-edit forms. */
export const CO_DISPLAY_LABEL = 'CO';

/**
 * Render label for a person. Returns "CO" for the CO; otherwise the rank
 * prefix joined with the name (rank optional, whitespace-trimmed).
 */
export function displayNameForUser(user: Readonly<DisplayNameUser>): string {
  if (user.role === Role.CO) return CO_DISPLAY_LABEL;
  const rank = user.rank?.trim();
  return rank ? `${rank} ${user.name}` : user.name;
}

/**
 * Avatar initials for a person. The CO renders as the literal letters "CO";
 * everyone else falls through to the standard name-based initials.
 */
export function initialsForUser(user: Readonly<Pick<DisplayNameUser, 'role' | 'name'>>): string {
  if (user.role === Role.CO) return CO_DISPLAY_LABEL;
  return initialsFromName(user.name);
}

/**
 * Project a person row into a `{name, rank}` pair for DTOs that ship BOTH
 * fields separately (the consumer renders `${rank ?? ''} ${name}` itself).
 * For the CO we override `name` to the role label and null the rank so the
 * consumer's join collapses to "CO"; for everyone else we ship the RAW name
 * + raw rank so the consumer's join doesn't double up (otherwise officers
 * render as e.g. "Capt Capt Rufait").
 *
 * Use this for assignee chips, board-member pickers, workload rows, the
 * directory — anywhere `name` + `rank` are rendered side-by-side. Use
 * `displayNameForUser` for single-string label fields (sender, actor,
 * author) where the consumer renders the value standalone.
 */
export function projectPersonRecord(user: Readonly<DisplayNameUser>): {
  name: string;
  rank: string | null;
} {
  if (user.role === Role.CO) return { name: CO_DISPLAY_LABEL, rank: null };
  return { name: user.name, rank: user.rank ?? null };
}

/**
 * Display label for a person's battalion appointment, or null if they don't
 * hold one. CO returns null (being CO subsumes any appointment chip — the
 * "CO" label already speaks for them).
 *
 * Accepts the appointment field directly OR a user-shaped record so callers
 * can pass `user` without unpacking. Unknown strings (defensive — JSON from
 * older snapshots, etc.) collapse to null.
 */
export function appointmentLabelFor(
  input: { role: string; appointment?: string | null } | string | null | undefined,
): string | null {
  if (input === null || input === undefined) return null;
  if (typeof input === 'string') {
    return isBattalionAppointment(input) ? APPOINTMENT_LABEL[input] : null;
  }
  if (input.role === Role.CO) return null;
  const raw = input.appointment ?? null;
  return raw && isBattalionAppointment(raw) ? APPOINTMENT_LABEL[raw] : null;
}

export type { BattalionAppointment };
